import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { nivelConsultorService } from '@/services/nivelConsultorService'
import {
  AtribuicaoNivelLinha,
  JUSTIFICATIVA_NIVEL_MAX,
  JUSTIFICATIVA_NIVEL_MIN,
  NivelConsultor,
  mensagemErroNivel,
} from '@/types/nivelConsultor'
import { ChipNivel } from './ChipNivel'

/** Valor do seletor para "Sem nível" (remoção). */
export const SEM_NIVEL = '__SEM_NIVEL__'

export interface DialogoAtribuirNivelProps {
  /** Consultor da linha; `null` = fechado. */
  linha: AtribuicaoNivelLinha | null
  /** Catálogo do ADMIN — o seletor mostra só os ATIVOS (§6). */
  niveis: NivelConsultor[]
  onFechar: () => void
  onSalvo: () => void
}

/**
 * Atribuir, trocar ou remover o nível de UM consultor (ADMIN — AN-10 §4/§10). Mostra o nível
 * atual; seletor só com níveis ativos + "Sem nível"; justificativa obrigatória (10–500, aparada)
 * também na remoção. Manda `nivelAtualEsperadoId` para o backend detectar dois admins na mesma
 * tela (409). Erros 409/422 aparecem dentro do diálogo.
 */
export const DialogoAtribuirNivel: FC<DialogoAtribuirNivelProps> = ({ linha, niveis, onFechar, onSalvo }) => {
  const [escolha, setEscolha] = useState('')
  const [justificativa, setJustificativa] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    setEscolha('')
    setJustificativa('')
    setErro(null)
  }, [linha?.consultorId])

  if (!linha) return null

  const atual = linha.nivel
  const ativos = niveis.filter((n) => n.ativo)
  const tamanho = justificativa.trim().length
  const justificativaOk = tamanho >= JUSTIFICATIVA_NIVEL_MIN && tamanho <= JUSTIFICATIVA_NIVEL_MAX
  const novoId = escolha === SEM_NIVEL ? null : escolha || undefined
  const mudou = novoId !== undefined && novoId !== (atual?.id ?? null)
  const podeSalvar = mudou && justificativaOk && !salvando
  const nomeConsultor = linha.nome || linha.consultorId

  const salvar = async () => {
    if (!podeSalvar) return
    setSalvando(true)
    setErro(null)
    try {
      await nivelConsultorService.atribuir(linha.consultorId, {
        nivelId: novoId ?? null,
        justificativa: justificativa.trim(),
        nivelAtualEsperadoId: atual?.id ?? null,
      })
      onSalvo()
    } catch (e) {
      setErro(mensagemErroNivel(e, 'Não foi possível alterar o nível'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>Alterar nível</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            Consultor: <strong>{nomeConsultor}</strong>
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Nível atual
            </Typography>
            {atual ? (
              <ChipNivel nome={atual.nome} cor={atual.cor} inativo={!atual.ativo} />
            ) : (
              <Typography variant="body2">Sem nível</Typography>
            )}
          </Box>
          {erro && (
            <Alert severity="error" role="alert">
              {erro}
            </Alert>
          )}
          <TextField
            label="Novo nível"
            select
            value={escolha}
            onChange={(e) => setEscolha(e.target.value)}
            fullWidth
            required
            helperText={ativos.length === 0 ? 'Nenhum nível ativo cadastrado' : ' '}
          >
            {ativos.map((n) => (
              <MenuItem key={n.id} value={n.id} disabled={n.id === atual?.id}>
                {n.nome}
                {n.id === atual?.id ? ' (atual)' : ''}
              </MenuItem>
            ))}
            <MenuItem value={SEM_NIVEL} disabled={!atual}>
              Sem nível{!atual ? ' (atual)' : ''}
            </MenuItem>
          </TextField>
          <TextField
            label="Justificativa"
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: JUSTIFICATIVA_NIVEL_MAX }}
            error={justificativa.length > 0 && !justificativaOk}
            helperText={`${tamanho}/${JUSTIFICATIVA_NIVEL_MAX} · mínimo de ${JUSTIFICATIVA_NIVEL_MIN} caracteres`}
          />
          <Alert severity="warning" variant="outlined">
            Não registre dados pessoais sensíveis na justificativa. Ela fica na auditoria.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={!podeSalvar}
          startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DialogoAtribuirNivel
